import { createContext, useContext } from "react";
import type { QueueEntry } from "./queue";

/**
 * What the application knows about working without a network (US-33.4).
 *
 * <p>In its own file so that the provider can be a component file and nothing else — the React Fast
 * Refresh rule (`react-refresh/only-export-components`) makes a module that exports both a component
 * and a hook lose its state on every edit, and this one holds a queue.
 */
export interface OfflineState {
  /** What the browser says about the network. */
  online: boolean;
  /** True when this session is a remembered identity rather than a signed-in one. */
  offline: boolean;
  /** When the data on screen was last fetched from the server, if any of it was. */
  savedAt?: number;
  /** This user's writes waiting to go out, oldest first. */
  entries: QueueEntry[];
  /** The entry the queue stopped on: the server refused it and somebody has to decide. */
  blocked?: QueueEntry;
  /** Writes waiting that belong to whoever used this device before. */
  othersCount: number;
  sending: boolean;
  lastSyncAt?: number;
  /** Tries the queue now — what the "Send now" button does. */
  flushNow: () => void;
  /** Throws one entry away (the blocked one, usually) and lets the rest through. */
  discard: (id: string) => void;
  /** Clears what the previous user left behind, without sending any of it. */
  discardOthers: () => void;
}

export const OfflineContext = createContext<OfflineState | null>(null);

export function useOffline(): OfflineState {
  const state = useContext(OfflineContext);
  if (!state) {
    throw new Error("useOffline must be used inside <OfflineProvider>");
  }
  return state;
}
